import client from '@common/utils/rest';
import { SpreadsheetDataImportResponseDto } from "../dto/data-import/response";
import { SpreadsheetExportNewLineType, SpreadsheetExportType } from "../dto/spreadsheet/export-type";
import { SaveFormatPayload, SpreadsheetDetailsImportDataParams } from '../dto/spreadsheet/type';
import { FIELD_CONTEXT, determineFieldContext } from "../../../common/utils/field-context";



export const importData = async (data: SpreadsheetDetailsImportDataParams): Promise<SpreadsheetDataImportResponseDto> => {
  try {
    const formData = new FormData();
    
    // Add file data
    if (data.fileData) {
      formData.append('fileData', data.fileData);
    }
    
    // Add settings as JSON string
    if (data.settings) {
      const settingsString = JSON.stringify(data.settings);
      formData.append('settings', settingsString);
    }

    const response = await fetch('api/spreadSheets/import', {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 404) {
        throw new Error(`Import endpoint not found. The backend import functionality may not be available. Please contact your administrator.`);
      }
      
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    
    // If the backend returns null data, it means the import started but didn't complete
    if (result.data === null) {
      return {
        errors: [],
        warnings: [],
        total: 0,
        processed: 0,
        completed: false,
        needsProgressCheck: true
      };
    }

    return result.data;
  } catch (error) {
    throw error;
  }
};

export const getImportProgress = async (importNumber: number): Promise<SpreadsheetDataImportResponseDto> => {
  try {
    const response = await fetch(`api/spreadSheets/importProgress?importNumber=${importNumber}`);
    
    if (!response.ok) {
      // If we get a 500 or 404 error, assume the import completed
      if (response.status === 500 || response.status === 404) {
        return {
          errors: [],
          warnings: [],
          total: 0,
          processed: 0,
          completed: true
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    // If there's an error checking progress, assume completion
    return {
      errors: [],
      warnings: [],
      total: 0,
      processed: 0,
      completed: true
    };
  }
};

export type SpreadsheetFieldDto = {
  fieldid: number;
  fieldname: string;
  fieldtype?: {
    typeid: number;
    typename: string;
  };
  system: boolean;
  systemField?: boolean;
  tableIndex?: number;
  type: number;
  fieldnameWithPath?: string;
  dataSourceNodeId?: number;
  fieldcontext: string; // Required field to prevent 500 errors
};

export type SpreadsheetSmartColumnDto = {
  id: number;
  type: number;
  width: number;
  field: SpreadsheetFieldDto;
  parentField?: SpreadsheetFieldDto;
  editable?: boolean;
  visible?: boolean;
  sortOrder?: number;
  sortAsc?: boolean;
  modulo?: number;
};

export type SpreadsheetWhereClauseDto = {
  fieldId: number;
  fieldName: string;
  type: number;
  system: boolean;
  tableIndex?: number;
  operator: number;
  value: string | {
    dataValue?: any;
    rawValue?: any;
    updateValue?: any;
    modified?: boolean;
    valid?: boolean;
    constraintViolation?: any;
    field?: any;
  };
  fieldClass: string;
  fieldcontext?: string; // Optional field context
  parentField?: SpreadsheetFieldDto; // Optional parent field for table subfields
};

export type SpreadsheetQueryModelDto = {
  whereClauses: SpreadsheetWhereClauseDto[];
  andOR?: number;
};

export type SpreadsheetDetailsExportDataParams = {
  columnModel: {
    smartColumns: SpreadsheetSmartColumnDto[];
  };
  queryModel?: SpreadsheetQueryModelDto;
  filter?: SpreadsheetQueryModelDto;
  showHeaders: boolean;
  delimiter?: string;
  exportType: SpreadsheetExportType;
  newLinesOptions?: SpreadsheetExportNewLineType;
  tableRowsOnly?: boolean;
  oneRowPerIndividual?: boolean;
  folderId?: number;
  familyId?: number;
  pss?: boolean; // Whether this is a pedigree spreadsheet
};

// Constants for export functionality
const DEFAULT_COLUMN_WIDTH = 100;
const DEFAULT_TYPE_ID = 1;
const SYSTEM_TYPE_NAME = 'SYSTEM';
const DEFAULT_TABLE_INDEX = 0;
const DEFAULT_DATA_SOURCE_NODE_ID = 0;
const ROW_COUNT_FIELD_ID = -599;
const ROW_COUNT_FIELD_NAME = 'Row Count';
const ROW_COUNT_COLUMN_WIDTH = 50;

// Utility function to download files
const downloadFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = sanitizeFileName(fileName);
  link.click();
  URL.revokeObjectURL(url);
};

const sanitizeFileName = (fileName: string): string => {
  return fileName
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/\.{2,}/g, '.');
};

export const exportData = (data: SpreadsheetDetailsExportDataParams): Promise<SpreadsheetDataImportResponseDto> => {                                                                                                   
  // Validate the input data
  if (!data.columnModel || !data.columnModel.smartColumns) {
    return Promise.reject(new Error('Invalid column model structure'));
  }

  if (!data.queryModel) {
    data.queryModel = { whereClauses: [] };
  }

  if (!data.filter) {
    data.filter = { whereClauses: [] };
  }

  // Ensure the column model has the required row count column
  const ensureRowCountColumn = (columnModel: any): any => {
    const hasRowCountColumn = columnModel.smartColumns.some((col: any) =>
      (col.field && col.field.fieldid === ROW_COUNT_FIELD_ID) || col.fieldid === ROW_COUNT_FIELD_ID
    );

    if (!hasRowCountColumn) {
      const rowCountColumn: any = {
        field: {
          fieldid: ROW_COUNT_FIELD_ID,
          fieldname: ROW_COUNT_FIELD_NAME,
          system: true,
          type: DEFAULT_TYPE_ID,
          fieldcontext: FIELD_CONTEXT.IDS_CONTEXT // Default context for system fields
        },
        width: ROW_COUNT_COLUMN_WIDTH,
        columnOrder: 0,
        sortOrder: 0,
        sortAsc: false,
        editable: false,
        visible: true
      };

      return {
        ...columnModel,
        smartColumns: [rowCountColumn, ...columnModel.smartColumns]
      };
    }

    return columnModel;
  };

  // Ensure the column model has the required structure
  const validatedColumnModel = ensureRowCountColumn(data.columnModel);

  // Create the request body with proper formatting
  const requestBody = {
    columnModel: {
      smartColumns: validatedColumnModel.smartColumns.map((col: any) => ({
        id: col.field?.fieldid || col.id,
        type: col.field?.type || col.type || DEFAULT_TYPE_ID,
        width: col.width || DEFAULT_COLUMN_WIDTH,
        sortOrder: col.sortOrder || 0,
        sortAsc: col.sortAsc !== undefined ? col.sortAsc : false,
        visible: col.visible !== undefined ? col.visible : true,
        editable: col.editable !== undefined ? col.editable : true,
        columnCount: col.columnCount || 0,
        columnOrder: col.columnOrder || 0,
        modulo: col.modulo || 0,
        field: {
          fieldid: col.field?.fieldid || 0,
          fieldname: col.field?.fieldname || '',
          system: col.field?.system || false,
          type: col.field?.type || DEFAULT_TYPE_ID,
          fieldcontext: col.field?.fieldcontext || determineFieldContext(data.pss || false, col.field?.type, col.field?.system)                                                                                           
        },
        // Include parentField information if it exists
        ...(col.parentField && {
          parentField: {
            fieldid: col.parentField.fieldid || 0,
            fieldname: col.parentField.fieldname || '',
            system: col.parentField.system || false,
            type: col.parentField.type || DEFAULT_TYPE_ID,
            fieldcontext: col.parentField.fieldcontext || determineFieldContext(data.pss || false, col.parentField.type, col.parentField.system)
          }
        })
      }))
    },
    queryModel: {
      whereClauses: data.queryModel.whereClauses.map((clause: any) => {
        // Handle both SpreadsheetTableWhereClause format (from table controller) and SpreadsheetWhereClauseDto format
        if (clause.field && typeof clause.field === 'object') {
          // Already in the correct format from table controller
          return {
            operator: clause.operator || 1,
            value: typeof clause.value === 'string' ? clause.value :
                (typeof clause.value === 'object' && clause.value !== null)
                    ? ((clause.value as any).dataValue !== undefined ? (clause.value as any).dataValue :
                        (clause.value as any).rawValue !== undefined ? (clause.value as any).rawValue :
                            (clause.value as any).updateValue !== undefined ? (clause.value as any).updateValue : "") : clause.value,
            fieldClass: clause.fieldClass || "class java.lang.String",
            field: {
              fieldid: clause.field.fieldid,
              type: clause.field.type,
              fieldcontext: clause.field.fieldcontext,
              system: clause.field.system
            },
            leftParenthesis: clause.leftParenthesis || false,
            rightParenthesis: clause.rightParenthesis || false,
            andOr: clause.andOr || clause.andOR || "",
            // Include parentField information if it exists in the clause
            ...(clause.parentField && {
              parentField: {
                fieldid: clause.parentField.fieldid || 0,
                fieldname: clause.parentField.fieldname || '',
                system: clause.parentField.system || false,
                type: clause.parentField.type || DEFAULT_TYPE_ID,
                fieldcontext: clause.parentField.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT
              }
            })
          };
        } else {
          // Legacy SpreadsheetWhereClauseDto format - transform it
          return {
            operator: clause.operator || 1,
            value: typeof clause.value === 'string' ? clause.value :
                (typeof clause.value === 'object' && clause.value !== null)
                    ? ((clause.value as any).dataValue !== undefined ? (clause.value as any).dataValue :
                        (clause.value as any).rawValue !== undefined ? (clause.value as any).rawValue :
                            (clause.value as any).updateValue !== undefined ? (clause.value as any).updateValue : "") : "",
            fieldClass: clause.fieldClass || "class java.lang.String",
            field: {
              fieldid: clause.fieldId,
              fieldname: clause.fieldName,
              system: clause.system,
              fieldcontext: clause.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT,
              type: clause.type || DEFAULT_TYPE_ID
            },
            leftParenthesis: false,
            rightParenthesis: false,
            andOr: "",
            // Include parentField information if it exists in the clause
            ...(clause.parentField && {
              parentField: {
                fieldid: clause.parentField.fieldid || 0,
                fieldname: clause.parentField.fieldname || '',
                system: clause.parentField.system || false,
                type: clause.parentField.type || DEFAULT_TYPE_ID,
                fieldcontext: clause.parentField.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT
              }
            })
          };
        }
      })
    },
    filter: {
      whereClauses: data.filter.whereClauses.map((clause: any) => {
        // Handle both SpreadsheetTableWhereClause format (from table controller) and SpreadsheetWhereClauseDto format
        if (clause.field && typeof clause.field === 'object') {
          // Already in the correct format from table controller
          return {
            operator: clause.operator || 1,
            value: typeof clause.value === 'string' ? clause.value :
                (typeof clause.value === 'object' && clause.value !== null)
                    ? ((clause.value as any).dataValue !== undefined ? (clause.value as any).dataValue :
                        (clause.value as any).rawValue !== undefined ? (clause.value as any).rawValue :
                            (clause.value as any).updateValue !== undefined ? (clause.value as any).updateValue : "") : clause.value,
            fieldClass: clause.fieldClass || "class java.lang.String",
            field: {
              fieldid: clause.field.fieldid,
              type: clause.field.type,
              fieldcontext: clause.field.fieldcontext,
              system: clause.field.system
            },
            leftParenthesis: clause.leftParenthesis || false,
            rightParenthesis: clause.rightParenthesis || false,
            andOr: clause.andOr || clause.andOR || "",
            // Include parentField information if it exists in the clause
            ...(clause.parentField && {
              parentField: {
                fieldid: clause.parentField.fieldid || 0,
                fieldname: clause.parentField.fieldname || '',
                system: clause.parentField.system || false,
                type: clause.parentField.type || DEFAULT_TYPE_ID,
                fieldcontext: clause.parentField.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT
              }
            })
          };
        } else {
          // Legacy SpreadsheetWhereClauseDto format - transform it
          return {
            operator: clause.operator || 1,
            value: typeof clause.value === 'string' ? clause.value :
                (typeof clause.value === 'object' && clause.value !== null)
                    ? ((clause.value as any).dataValue !== undefined ? (clause.value as any).dataValue :
                        (clause.value as any).rawValue !== undefined ? (clause.value as any).rawValue :
                            (clause.value as any).updateValue !== undefined ? (clause.value as any).updateValue : "") : "",
            fieldClass: clause.fieldClass || "class java.lang.String",
            field: {
              fieldid: clause.fieldId,
              fieldname: clause.fieldName,
              system: clause.system,
              fieldcontext: clause.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT,
              type: clause.type || DEFAULT_TYPE_ID
            },
            leftParenthesis: false,
            rightParenthesis: false,
            andOr: "",
            // Include parentField information if it exists in the clause
            ...(clause.parentField && {
              parentField: {
                fieldid: clause.parentField.fieldid || 0,
                fieldname: clause.parentField.fieldname || '',
                system: clause.parentField.system || false,
                type: clause.parentField.type || DEFAULT_TYPE_ID,
                fieldcontext: clause.parentField.fieldcontext || FIELD_CONTEXT.IDS_CONTEXT
              }
            })
          };
        }
      })
    },
    pss: data.pss || false,
    exportType: data.exportType,
    delimiter: data.delimiter || '\t',
    newLineOption: data.newLinesOptions || 2,
    showHeaders: data.showHeaders || false,
    tableRowsOnly: data.tableRowsOnly || false,
    oneRowPerIndividual: data.oneRowPerIndividual || false,
    folderId: data.folderId || 0,
    familyId: data.familyId || 0
  };

  // Use fetch directly for file downloads to handle binary responses correctly
  return fetch('api/spreadSheets/export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  })
  .then(async response => {
    if (response.ok) {
      // Get the blob for file download
      const blob = await response.blob();
      const fileName = getFileName(data.exportType);
      
      // Use the downloadFile utility to handle the file download
      downloadFile(blob, fileName);
      
      return {
        errors: [],
        warnings: [],
        total: 0,
        processed: 0,
      };
    } else {
      // For error status codes (400, 500, etc.), throw an error
      let errorMessage = `Export failed: ${response.status} ${response.statusText}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch (e) {
        // Silent catch for error text reading
      }
      
      throw new Error(errorMessage);
    }
  })
  .catch(error => {
    // Re-throw the error so it can be caught by the calling function
    throw error;
  });
};

const getContentType = (exportType: SpreadsheetExportType): string => {
  switch (exportType) {
    case SpreadsheetExportType.xlsx:
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case SpreadsheetExportType.xls:
      return 'application/vnd.ms-excel';
    case SpreadsheetExportType.txtTab:
    case SpreadsheetExportType.txtComma:
    case SpreadsheetExportType.txtOther:
      return 'text/plain';
    default:
      return 'text/plain';
  }
};

const getFileName = (exportType: SpreadsheetExportType): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  switch (exportType) {
    case SpreadsheetExportType.xlsx:
      return `spreadsheet-export-${timestamp}.xlsx`;
    case SpreadsheetExportType.xls:
      return `spreadsheet-export-${timestamp}.xls`;
    case SpreadsheetExportType.txtTab:
      return `spreadsheet-export-${timestamp}.txt`;
    case SpreadsheetExportType.txtComma:
      return `spreadsheet-export-${timestamp}.csv`;
    case SpreadsheetExportType.txtOther:
      return `spreadsheet-export-${timestamp}.txt`;
    default:
      return `spreadsheet-export-${timestamp}.txt`;
  }
};

export const saveFormat = (payload: SaveFormatPayload): Promise<void> => {
  return client({
    path: "api/formats/spreadSheets/save",
    method: "POST",
    entity: payload,
  }).then(
    (response: { entity: { data: SaveFormatPayload } }) => response.entity.data
  );
}
